import type { AdminPaymentListItem, AdminPaymentListResponse } from '@inflip/contracts/admin';
import { ORDER_STATUS_LABELS, PaymentKind, PaymentMethod, PaymentStatus } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { won } from '../components/confirm-deposit-dialog';
import { DataTable } from '../components/data-table';
import { DateRangeFilter } from '../components/date-range-filter';
import {
  Button,
  Chip,
  EmptyState,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { ORDER_STATUS_TEXT, PAYMENT_STATUS_META } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const PAGE_SIZE = 20;

const KIND_LABELS: Record<PaymentKind, string> = {
  [PaymentKind.INITIAL]: '주문 대금',
  [PaymentKind.ADDON]: '추가금',
};

/** 상태가 바뀐 시각 — 확인은 확인 시각, 만료·취소·실패는 따로 시각이 없어 마지막 변경 시각 */
function settledAt(p: AdminPaymentListItem): string | null {
  if (p.status === PaymentStatus.CONFIRMED) return p.confirmedAt;
  if (p.status === PaymentStatus.PENDING) return null;
  return p.updatedAt;
}

const columns: ColumnDef<AdminPaymentListItem>[] = [
  {
    id: 'status',
    header: '상태',
    size: 110,
    cell: ({ row }) => {
      const p = row.original;
      const at = settledAt(p);
      return (
        <>
          <span className={`text-xs font-bold ${PAYMENT_STATUS_META[p.status].cls}`}>
            {PAYMENT_STATUS_META[p.status].label}
          </span>
          {at && (
            <div className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">
              {formatDateTime(at)}
            </div>
          )}
          {p.status === PaymentStatus.CONFIRMED && p.confirmedByName && (
            <div className="text-[11px] text-muted-foreground">{p.confirmedByName}</div>
          )}
        </>
      );
    },
  },
  {
    id: 'kind',
    header: '종류',
    size: 90,
    cell: ({ row }) => (
      <>
        <span className="text-sm font-semibold">{KIND_LABELS[row.original.kind]}</span>
        {row.original.method === PaymentMethod.CARD && (
          <div className="text-[11px] text-muted-foreground">카드</div>
        )}
      </>
    ),
  },
  {
    id: 'order',
    header: '주문',
    cell: ({ row }) => {
      const { orders, orderGroupId } = row.original;
      const first = orders[0];
      return (
        <>
          {first && (
            <>
              <Link
                to="/orders/$orderId"
                params={{ orderId: first.id }}
                className="font-mono text-xs font-medium text-brand hover:underline"
              >
                {first.id.slice(0, 8)}
              </Link>
              <div
                className={`mt-0.5 text-[11px] font-semibold ${ORDER_STATUS_TEXT[first.status]}`}
              >
                {ORDER_STATUS_LABELS[first.status]}
              </div>
            </>
          )}
          {orderGroupId && orders.length > 1 && (
            <Link
              to="/orders"
              search={{ q: orderGroupId, from: '', to: '' }}
              className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
            >
              묶음 {orders.length}건 <span className="font-mono">{orderGroupId.slice(0, 8)}</span>
            </Link>
          )}
        </>
      );
    },
  },
  {
    id: 'listing',
    header: '상품',
    cell: ({ row }) => (
      <span className="block truncate text-sm">{row.original.listingTitle ?? '(삭제됨)'}</span>
    ),
  },
  {
    id: 'buyer',
    header: '구매자',
    cell: ({ row }) =>
      row.original.buyerId ? (
        <>
          <Link
            to="/users/$userId"
            params={{ userId: row.original.buyerId }}
            className="block truncate font-semibold text-brand hover:underline"
          >
            {row.original.buyerName ?? '—'}
          </Link>
          <div className="truncate text-xs text-muted-foreground">
            {row.original.buyerEmail ?? ''}
          </div>
        </>
      ) : (
        '—'
      ),
  },
  {
    id: 'amount',
    header: '금액',
    meta: { align: 'right', tdClass: 'whitespace-nowrap tabular-nums' },
    cell: ({ row }) => <div className="font-bold">{won(row.original.amount)}</div>,
  },
  {
    id: 'depositor',
    header: '입금자명 · 사유',
    cell: ({ row }) => {
      const p = row.original;
      const differs =
        p.receivedDepositorName != null && p.receivedDepositorName !== p.depositReference;
      return (
        <>
          <div className="truncate text-sm">{p.depositReference ?? '—'}</div>
          {differs && (
            <div className="truncate text-xs text-amber-700">
              실제 {p.receivedDepositorName}
              {p.depositorOverrideReason ? ` · ${p.depositorOverrideReason}` : ''}
            </div>
          )}
          {p.memo && <div className="truncate text-xs text-muted-foreground">{p.memo}</div>}
          {p.failReason && <div className="truncate text-xs text-rose-600">{p.failReason}</div>}
        </>
      );
    },
  },
  {
    id: 'due',
    header: '입금 기한',
    cell: ({ row }) =>
      row.original.depositDueAt ? formatDateTime(row.original.depositDueAt) : '—',
    meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
  },
  {
    id: 'createdAt',
    header: '생성',
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
  },
];

/**
 * 입금 관리 — 주문 대금·추가금 입금 한 건이 한 줄. 주문 목록은 지금 상태만 보여 주니,
 * 입금 대기에서 확인·만료·취소로 넘어간 입금도 여기서 계속 찾아본다. 입금 확인은 주문 상세에서 한다
 */
export function PaymentsPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<PaymentStatus | ''>('status', '');
  const [kind, setKind] = useListViewState<PaymentKind | ''>('kind', '');
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  useEffect(() => setQ(searchTerm), [searchTerm]);
  const [page, setPage] = useListViewState('page', 1);
  const [dateFrom, setDateFrom] = useListViewState('from', '');
  const [dateTo, setDateTo] = useListViewState('to', '');
  const [result, setResult] = useState<AdminPaymentListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setError(null);
    api
      .listPayments(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(filter ? { status: filter } : {}),
        ...(kind ? { kind } : {}),
        ...(searchTerm ? { q: searchTerm } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      })
      .then((r) => !cancelled && setResult(r))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [session, filter, kind, searchTerm, page, dateFrom, dateTo]);

  if (!session) return null;
  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="입금 관리"
        description={
          result
            ? `${result.total.toLocaleString('ko-KR')}건 · 주문 대금·추가금 입금 기록 (확인·만료·취소 포함)`
            : '주문 대금·추가금 입금 기록 (확인·만료·취소 포함)'
        }
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q);
            }}
          >
            <input
              className={`w-72 ${inputCls}`}
              placeholder="주문·묶음 번호 / 입금자명 / 구매자 / 상품"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <Chip
          active={filter === ''}
          onClick={() => {
            setPage(1);
            setFilter('');
          }}
        >
          전체
        </Chip>
        {Object.values(PaymentStatus).map((s) => (
          <Chip
            key={s}
            active={filter === s}
            onClick={() => {
              setPage(1);
              setFilter(s);
            }}
          >
            {PAYMENT_STATUS_META[s].label}
          </Chip>
        ))}
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(from, to) => {
            setPage(1);
            setDateFrom(from);
            setDateTo(to);
          }}
        />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {(['', PaymentKind.INITIAL, PaymentKind.ADDON] as const).map((k) => (
          <Chip
            key={k || 'all'}
            active={kind === k}
            onClick={() => {
              setPage(1);
              setKind(k);
            }}
          >
            {k ? KIND_LABELS[k] : '모든 종류'}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}
      {!result && !error && <LoadingRow />}

      {result && rows.length === 0 ? (
        <EmptyState message="조건에 맞는 입금이 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(r) => r.id} />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </main>
  );
}
